import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import PhoneInTalkOutlined from "@material-ui/icons/PhoneInTalkOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AddIcon from "@material-ui/icons/AddRounded";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/OpenInNewRounded";

import api from "../../services/api";
import { useConfirm } from "../../components/ConfirmDialog";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { formatPhone } from "../../utils/formatPhone";
import useMenuStyles from "../../components/menuStyles";
import AssignNumberDialog from "./AssignNumberDialog";

// Aba "Canais" do agente (spec docs/handoff-canais-agente.md §3.1). Mostra os
// canais vinculados (voz + WhatsApp) e deixa criar/remover vínculo. 1 agente ⇄ 1
// número: um conector WhatsApp atende um só agente. Voz = PhoneNumber (reusa o
// AssignNumberDialog); WhatsApp = Connector.config.agentId.
//
// Layout: segue o padrão de duas colunas das abas irmãs (Geral/Voz do
// AgentEditor) — rótulo+descrição à esquerda, controles à direita, com linha
// divisória entre seções.
const selectMenuProps = menuCls => ({
  classes: { paper: menuCls.selectPaper },
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
});

const useStyles = makeStyles(theme => {
  const a = theme.app || {};
  return {
    wrap: { paddingBottom: 0 }, // as seções do editor (Voz, Modelo…) seguem logo abaixo
    // mesma grade das seções de Geral/Voz (AgentEditor.section)
    section: {
      display: "grid", gridTemplateColumns: "260px 1fr", gap: 44, alignItems: "start",
      borderTop: `1px solid ${a.border || "#E6E9ED"}`, padding: "26px 0",
      "@media (max-width: 860px)": { gridTemplateColumns: "1fr", gap: 14 },
    },
    secLabel: { fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: a.textSecondary || "#5B6470" },
    secDesc: { fontSize: 13.5, color: a.textTertiary || "#929AA4", marginTop: 8, lineHeight: 1.5 },
    secRight: { minWidth: 0, maxWidth: 520 },

    cardList: { display: "flex", flexDirection: "column", gap: 10 },
    card: {
      display: "flex", alignItems: "center", gap: 14, background: "#fff",
      border: `1px solid ${a.border || "#E6E9ED"}`, borderRadius: 12, padding: "12px 16px",
    },
    icon: {
      width: 38, height: 38, flexShrink: 0, borderRadius: 999, background: a.lineSoft || "#F1F4F2",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      "& svg": { fontSize: 20, color: a.textTertiary || "#929AA4" },
    },
    waIcon: { background: "rgba(37,211,102,0.12)", "& svg": { color: "#1E9E62" } },
    main: { flex: 1, minWidth: 0 },
    title: { fontSize: 14, color: a.ink || "#2c3036", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    sub: { fontSize: 12.5, color: a.textTertiary || "#929AA4", marginTop: 3 },

    // vazio compacto (antes era uma caixa tracejada enorme de largura cheia)
    empty: {
      display: "flex", alignItems: "center", gap: 10, background: a.chatBg || "#F7F8FA",
      border: `1px dashed ${a.borderInput || "#cfd6dd"}`, borderRadius: 12, padding: "14px 16px",
      color: a.textTertiary || "#929AA4", fontSize: 13, lineHeight: 1.5,
    },
    emptyDot: {
      width: 8, height: 8, borderRadius: 999, flexShrink: 0, background: a.border || "#E6E9ED",
    },

    actions: { marginTop: 14 },
    field: { width: "100%", maxWidth: 320, margin: 0 },
    primaryBtn: {
      flexShrink: 0, textTransform: "none", fontSize: 13, fontWeight: 600, color: "#fff",
      background: a.dark || "#1B222A", borderRadius: 8, padding: "8px 18px", boxShadow: "none", height: 40,
      "&:hover": { background: a.ink || "#0F141A", boxShadow: "none" },
      "&.Mui-disabled": { background: a.border || "#E6E9ED", color: a.textTertiary || "#929AA4" },
    },
    hint: { fontSize: 12.5, color: a.textTertiary || "#929AA4", marginTop: 8, lineHeight: 1.4 },
    link: { color: a.green || "#2e7d32", cursor: "pointer", fontWeight: 600 },
    iconAct: { color: a.textTertiary || "#929AA4", "&:hover": { color: a.ink || "#2c3036" } },
    dangerAct: { color: a.textTertiary || "#929AA4", "&:hover": { color: a.danger || "#C0453B" } },
  };
});

const HISTORY_OPTS = [
  { value: "considerar", label: "Considerar histórico anterior", hint: "A IA lê os últimos turnos e avalia se têm relação com a nova mensagem — reutiliza se sim, descarta se não." },
  { value: "usar", label: "Usar histórico anterior", hint: "Sempre retoma a conversa de onde parou." },
  { value: "ignorar", label: "Ignorar histórico anterior", hint: "Sempre começa do zero." },
];

// only: "voz" | "whatsapp" — a aba Canais do editor tem uma sub-aba por canal
const AgentCanais = ({ only, agentId, agentName, projectId, numbers = [], onChange, historyValue = "considerar", onHistoryChange }) => {
  const showVoz = only !== "whatsapp";
  const showWa = only !== "voz";
  const classes = useStyles();
  const menu = useMenuStyles();
  const history = useHistory();
  const [whatsapp, setWhatsapp] = useState(null);
  const [available, setAvailable] = useState([]);
  const [waMenu, setWaMenu] = useState(null); // âncora do menu "Vincular WhatsApp"
  const [busy, setBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // { connectorId, message } p/ transferir vínculo
  const { confirm: confirmDialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/agents/${agentId}/channels`);
      setWhatsapp(data.whatsapp || null);
      setAvailable(data.available || []);
    } catch (err) {
      toastError(err);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const bind = async (connectorId, force = false) => {
    setBusy(true);
    try {
      await api.put(`/agents/${agentId}/channels/whatsapp`, { connectorId, force });
      toast.success("WhatsApp vinculado ao agente.");
      setConfirm(null);
      load();
    } catch (err) {
      // 409 = conector já atende outro agente → confirma transferência (sem popup nativo)
      if (err && err.response && err.response.status === 409) {
        setConfirm({ connectorId, message: err.response.data && err.response.data.error });
      } else {
        toastError(err);
      }
    } finally {
      setBusy(false);
    }
  };

  const unbind = async () => {
    setBusy(true);
    try {
      await api.delete(`/agents/${agentId}/channels/whatsapp`);
      toast.success("WhatsApp desvinculado.");
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const removeNumber = async n => {
    const ok = await confirmDialog({
      title: "Remover número",
      message: n.test
        ? `Desvincular ${formatPhone(n.number)} (número de teste) deste agente?`
        : `Desvincular ${formatPhone(n.number)} deste agente? Ele deixará de receber as chamadas (a automação de atendimento continua em Automações).`,
      confirmText: "Remover",
      danger: true,
    });
    if (!ok) return;
    try {
      if (n.test) await api.delete("/numbers/test-number/binding");
      else await api.put(`/numbers/${n.id}`, { inboundApplicationId: null });
      if (onChange) onChange();
    } catch (err) {
      toastError(err);
    }
  };

  const histOpt =HISTORY_OPTS.find(o => o.value === (historyValue || "considerar")) || HISTORY_OPTS[0];

  return (
    <div className={classes.wrap}>
      {/* VOZ */}
      {showVoz && (
      <div className={classes.section}>
        <div>
          <div className={classes.secLabel}>Telefone (voz)</div>
          <div className={classes.secDesc}>Número que faz e recebe chamadas de voz para este agente.</div>
        </div>
        <div className={classes.secRight}>
          {numbers.length === 0 ? (
            <div className={classes.empty}>
              <span className={classes.emptyDot} />
              Nenhum telefone vinculado ainda.
            </div>
          ) : (
            <div className={classes.cardList}>
              {numbers.map(n => (
                <div key={n.id || n.number} className={classes.card}>
                  <span className={classes.icon}><PhoneInTalkOutlined /></span>
                  <div className={classes.main}>
                    <div className={classes.title}>{formatPhone(n.number)}</div>
                    <div className={classes.sub}>
                      {n.test ? (
                        "Número de teste (grátis) · só conversa com o seu celular verificado"
                      ) : (
                        <>
                          {n.city ? `${n.city} · ` : ""}
                          <span className={classes.link} onClick={() => history.push(`/applications/${n.appId}`)}>editar atendimento</span>
                        </>
                      )}
                    </div>
                  </div>
                  {!n.test && (
                    <Tooltip title="Abrir automação de atendimento">
                      <IconButton size="small" className={classes.iconAct} onClick={() => history.push(`/applications/${n.appId}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Remover número">
                    <IconButton size="small" className={classes.dangerAct} onClick={() => removeNumber(n)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              ))}
            </div>
          )}
          <div className={classes.actions}>
            <Button className={classes.primaryBtn} onClick={() => setAssignOpen(true)}>
              {numbers.length ? "Vincular outro telefone" : "Vincular telefone"}
            </Button>
          </div>
        </div>
      </div>

      )}

      {/* WHATSAPP */}
      {showWa && (
      <div className={classes.section}>
        <div>
          <div className={classes.secLabel}>WhatsApp</div>
          <div className={classes.secDesc}>
            O agente é o dono do número: atende quem escreve e conduz as conversas que o fluxo iniciar. Um número atende um só agente.
          </div>
        </div>
        <div className={classes.secRight}>
          {whatsapp ? (
            <div className={classes.card}>
              <span className={`${classes.icon} ${classes.waIcon}`}><WhatsAppIcon /></span>
              <div className={classes.main}>
                <div className={classes.title}>{whatsapp.name}</div>
                <div className={classes.sub}>WhatsApp (QRCode) · atendido por este agente</div>
              </div>
              <Tooltip title="Desvincular WhatsApp">
                <span>
                  <IconButton size="small" className={classes.dangerAct} onClick={unbind} disabled={busy}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          ) : (
            <div className={classes.empty}>
              <span className={classes.emptyDot} />
              {available.length ? "Nenhum WhatsApp vinculado ainda." : "Nenhum conector WhatsApp (QRCode) disponível."}
            </div>
          )}
          {!whatsapp && (
            <div className={classes.actions}>
              {available.length ? (
                <Button className={classes.primaryBtn} disabled={busy} onClick={e => setWaMenu(e.currentTarget)}>Vincular WhatsApp</Button>
              ) : (
                <Button className={classes.primaryBtn} onClick={() => history.push("/connectors")}>Conectar WhatsApp</Button>
              )}
            </div>
          )}
          <Menu
            anchorEl={waMenu}
            open={!!waMenu}
            onClose={() => setWaMenu(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{ className: menu.paper }}
            MenuListProps={{ disablePadding: true }}
          >
            {available.map(c => (
              <MenuItem key={c.id} className={menu.item} onClick={() => { setWaMenu(null); bind(c.id); }}>
                <WhatsAppIcon /> {c.name}
              </MenuItem>
            ))}
            <div className={menu.divider} />
            <MenuItem className={menu.item} onClick={() => { setWaMenu(null); history.push("/connectors"); }}>
              <AddIcon /> Conectar outro número
            </MenuItem>
          </Menu>
        </div>
      </div>

      )}

      {/* HISTÓRICO em conversas iniciadas pelo cliente (spec §3.5) */}
      {showWa && (
      <div className={classes.section}>
        <div>
          <div className={classes.secLabel}>Conversas iniciadas pelo cliente</div>
          <div className={classes.secDesc}>
            Quando o cliente escreve depois que uma conversa esfriou, o que a IA faz com o histórico anterior.
          </div>
        </div>
        <div className={classes.secRight}>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Histórico anterior"
            className={classes.field}
            style={{ maxWidth: 360 }}
            value={historyValue || "considerar"}
            onChange={e => onHistoryChange && onHistoryChange(e.target.value)}
            SelectProps={{ MenuProps: selectMenuProps(menu) }}
          >
            {HISTORY_OPTS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <div className={classes.hint}>{histOpt.hint}</div>
        </div>
      </div>

      )}

      <AssignNumberDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        agentId={agentId}
        agentName={agentName}
        projectId={projectId}
        onDone={() => { setAssignOpen(false); if (onChange) onChange(); load(); }}
      />

      {/* confirmação de transferência de vínculo (conector já atende outro agente) */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>Transferir o vínculo?</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm && confirm.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancelar</Button>
          <Button color="primary" variant="contained" disableElevation disabled={busy}
            onClick={() => confirm && bind(confirm.connectorId, true)}>
            Transferir para este agente
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AgentCanais;
